import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiAlertCircle, FiCheck, FiRefreshCw, FiSave } from "react-icons/fi";
import axios from "../api/axiosInstance";
import { usePmsTheme } from "./PmsThemeContext";

const normalizeCode = (value) => String(value || "").trim().toUpperCase();
const SECONDARY_TO_BASE = "secondary_to_base";
const BASE_TO_SECONDARY = "base_to_secondary";

const reciprocalRate = (value) => {
  const numeric = Number(value);
  if (!(numeric > 0) || !Number.isFinite(numeric)) return "";
  return (1 / numeric).toFixed(12).replace(/\.?0+$/, "");
};

const displayRate = (canonicalRate, direction) =>
  direction === BASE_TO_SECONDARY ? reciprocalRate(canonicalRate) : String(canonicalRate || "");

const LOCK_NOTICE =
  "Existing reservations keep the currency and exchange rate locked when they were created.";

const uniqueCodes = (values) =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [values])
        .map(normalizeCode)
        .filter((code) => /^[A-Z]{3}$/.test(code))
    )
  );

const firstCurrencyCode = (...values) =>
  values
    .flatMap((value) => uniqueCodes(value))
    .find(Boolean) || "";

const humanizeDependencyKey = (key, count) => {
  const singular = Number(count) === 1;
  const labels = {
    reservation_drafts: singular ? "reservation draft" : "reservation drafts",
    drafts: singular ? "reservation draft" : "reservation drafts",
    quotes: singular ? "open quote" : "open quotes",
    booking_quotes: singular ? "open quote" : "open quotes",
    pending_payments: singular ? "pending payment" : "pending payments",
    payments: singular ? "open payment" : "open payments",
    open_workflows: singular ? "open workflow" : "open workflows",
  };
  if (labels[key]) return labels[key];
  return String(key || "dependency").replace(/_/g, " ");
};

const liveDependencySummary = (details = {}) => {
  const countSources = [details.counts, details.dependency_counts, details.live_dependencies]
    .filter((value) => value && typeof value === "object" && !Array.isArray(value));
  const idsSources = [details.ids, details.dependency_ids]
    .filter((value) => value && typeof value === "object" && !Array.isArray(value));
  const seen = new Set();
  const summaries = [];

  countSources.forEach((source) => {
    Object.entries(source).forEach(([key, rawValue]) => {
      const count = Array.isArray(rawValue)
        ? rawValue.length
        : Number(rawValue?.count ?? rawValue);
      if (!Number.isFinite(count) || count <= 0 || seen.has(key)) return;
      seen.add(key);
      const inlineIds = Array.isArray(rawValue)
        ? rawValue
        : Array.isArray(rawValue?.ids)
          ? rawValue.ids
          : null;
      const externalIds = idsSources
        .map((sourceIds) => sourceIds[key])
        .find(Array.isArray);
      const ids = (inlineIds || externalIds || []).filter((id) => id !== null && id !== undefined);
      summaries.push(
        `${count} ${humanizeDependencyKey(key, count)}${
          ids.length ? ` (IDs: ${ids.join(", ")})` : ""
        }`
      );
    });
  });

  return summaries.join(", ");
};

const historySourceSummary = (sources = {}) =>
  Object.entries(sources || {})
    .map(([key, value]) => {
      const rows = Number(value?.rows ?? value?.count ?? value);
      if (!Number.isFinite(rows) || rows <= 0) return null;
      const label = key === "revenue_daily"
        ? "Revenue ledger"
        : key === "reservation_currency_snapshots"
          ? "Reservation FX locks"
          : key === "business_unit_fx_rate_sets"
            ? "Saved FX rate sets"
          : String(key).replace(/_/g, " ");
      const codes = uniqueCodes(value?.currency_codes || value?.currencies);
      return `${label}: ${rows} row${rows === 1 ? "" : "s"}${
        codes.length ? ` (${codes.join(", ")})` : ""
      }`;
    })
    .filter(Boolean)
    .join(" · ");

export const priceConflictMessage = ({ details, baseCurrency, languageId }) => {
  const labels = languageId === "es"
    ? [
        { keys: ["room_rates"], label: "Tarifas" },
        { keys: ["absolute_extras", "extras"], label: "Extras" },
        { keys: ["charge_types"], label: "Cargos" },
      ]
    : languageId === "ca"
      ? [
          { keys: ["room_rates"], label: "Tarifes" },
          { keys: ["absolute_extras", "extras"], label: "Extres" },
          { keys: ["charge_types"], label: "Càrrecs" },
        ]
      : [
          { keys: ["room_rates"], label: "Rates" },
          { keys: ["absolute_extras", "extras"], label: "Extras" },
          { keys: ["charge_types"], label: "Charges" },
        ];
  const groups = labels
    .map(({ keys, label }) => {
      const rows = keys
        .map((key) => details?.[key])
        .find(Array.isArray) || [];
      if (!rows.length) return null;
      const items = rows.map((row) => {
        const name = row.name || row.rate_code || row.code || `#${row.id}`;
        return `${name} (${normalizeCode(row.currency) || "no valid currency"})`;
      });
      return `${label}: ${items.join(", ")}`;
    })
    .filter(Boolean);

  if (languageId === "es") {
    return `No se puede guardar esta configuración porque hay elementos activos del catálogo que usan una moneda no válida o que se intenta deshabilitar. ${groups.join(" · ")}. Cambia o desactiva esos elementos antes de retirar la moneda. Las tarifas y los extras deben usar una moneda habilitada con tipo de cambio; los cargos deben usar ${baseCurrency}. Las reservas existentes conservan la moneda y el tipo de cambio bloqueados al crearlas.`;
  }
  if (languageId === "ca") {
    return `No es pot desar aquesta configuració perquè hi ha elements actius del catàleg que fan servir una moneda no vàlida o que s'intenta deshabilitar. ${groups.join(" · ")}. Canvia o desactiva aquests elements abans de retirar la moneda. Les tarifes i els extres han de fer servir una moneda habilitada amb tipus de canvi; els càrrecs han de fer servir ${baseCurrency}. Les reserves existents conserven la moneda i el tipus de canvi bloquejats en crear-les.`;
  }
  return `This currency configuration cannot be saved because active catalog items use an invalid currency or one you are trying to disable. ${groups.join(" · ")}. Change or deactivate those items before removing the currency. Rates and extras must use an enabled currency with an exchange rate; charges must use ${baseCurrency}. ${LOCK_NOTICE}`;
};

export const currencySettingsErrorMessage = ({
  payload = {},
  baseCurrency,
  savedBaseCurrency,
  languageId,
} = {}) => {
  const details = payload?.details || {};
  const requestedBase = firstCurrencyCode(
    details.requested_base_currency_code,
    baseCurrency
  );
  const existingBase = firstCurrencyCode(
    details.established_base_currency_code,
    details.current_base_currency_code,
    details.previous_base_currency_code,
    details.existing_base_currency_code,
    details.historical_base_currency_code,
    savedBaseCurrency
  );

  if (payload.code === "PMS_PRICE_CURRENCY_CONFLICT") {
    return priceConflictMessage({ details, baseCurrency: requestedBase || baseCurrency, languageId });
  }

  if (payload.code === "BASE_CURRENCY_MIGRATION_REQUIRED") {
    const change = existingBase && requestedBase
      ? ` from ${existingBase} to ${requestedBase}`
      : "";
    const footprint = details.financial_footprint || {};
    const footprintParts = [
      footprint.settings_activated ? "currency settings already activated" : null,
      Number(footprint.reservations) > 0 ? `${footprint.reservations} reservation${Number(footprint.reservations) === 1 ? "" : "s"}` : null,
      Number(footprint.drafts) > 0 ? `${footprint.drafts} draft${Number(footprint.drafts) === 1 ? "" : "s"}` : null,
      Number(footprint.revenue_ledger_rows) > 0 ? `${footprint.revenue_ledger_rows} revenue-ledger row${Number(footprint.revenue_ledger_rows) === 1 ? "" : "s"}` : null,
      Number(footprint.reservation_fx_snapshot_rows) > 0 ? `${footprint.reservation_fx_snapshot_rows} reservation FX lock${Number(footprint.reservation_fx_snapshot_rows) === 1 ? "" : "s"}` : null,
      Number(footprint.reservation_group_payment_rows) > 0 ? `${footprint.reservation_group_payment_rows} group payment${Number(footprint.reservation_group_payment_rows) === 1 ? "" : "s"}` : null,
      Number(footprint.reservation_group_payment_intent_rows) > 0 ? `${footprint.reservation_group_payment_intent_rows} provider payment intent${Number(footprint.reservation_group_payment_intent_rows) === 1 ? "" : "s"}` : null,
      Number(footprint.reservation_group_payment_event_rows) > 0 ? `${footprint.reservation_group_payment_event_rows} provider payment event${Number(footprint.reservation_group_payment_event_rows) === 1 ? "" : "s"}` : null,
      Number(footprint.reservation_payment_allocation_rows) > 0 ? `${footprint.reservation_payment_allocation_rows} payment allocation${Number(footprint.reservation_payment_allocation_rows) === 1 ? "" : "s"}` : null,
      Number(footprint.fx_rate_set_rows) > 0 ? `${footprint.fx_rate_set_rows} saved rate set${Number(footprint.fx_rate_set_rows) === 1 ? "" : "s"}` : null,
    ].filter(Boolean);
    return `The hotel base currency cannot be changed${change} from Settings after monetary activity exists${footprintParts.length ? ` (${footprintParts.join(", ")})` : ""}. Adding a secondary currency or updating its exchange rate does not change the base currency. Keep ${existingBase || "the current currency"} as base, or use a separately reviewed monetary-data migration only if the accounting base genuinely must change. ${LOCK_NOTICE}`;
  }

  if (payload.code === "BASE_CURRENCY_HISTORY_INCONSISTENT") {
    const codes = uniqueCodes(
      details.reliable_base_currency_codes ||
        details.reliable_currency_codes ||
        details.historical_currency_codes ||
        details.currency_codes
    );
    const sources = historySourceSummary(details.sources);
    const invalidHistoryRows =
      details.invalid_reliable_base_rows || details.invalid_history_rows;
    const invalidRows = Array.isArray(invalidHistoryRows)
      ? invalidHistoryRows.reduce(
          (total, row) => total + Math.max(0, Number(row?.rows) || 0),
          0
        )
      : Number(invalidHistoryRows || 0);
    const diagnostics = [
      codes.length ? `Detected currencies: ${codes.join(", ")}.` : null,
      invalidRows > 0 ? `Invalid or missing currency rows: ${invalidRows}.` : null,
      sources ? `${sources}.` : null,
    ].filter(Boolean).join(" ");
    return `Currency settings were not changed because reliable PMS financial history is inconsistent. ${diagnostics} Reconcile the reported financial history before saving again. ${LOCK_NOTICE} Immutable reservation snapshots alone do not require a migration.`;
  }

  if (payload.code === "CURRENCY_IN_USE_BY_OPEN_WORKFLOW") {
    const blockedCurrencies = uniqueCodes(
      details.currency_codes ||
        details.blocked_currency_codes ||
        details.removed_currency_codes ||
        details.currency_code
    );
    const dependencies = liveDependencySummary(details);
    return `${blockedCurrencies.length ? blockedCurrencies.join(", ") : "This currency"} cannot be disabled because open PMS work still depends on it${dependencies ? `: ${dependencies}` : ""}. Finish, cancel or reprice the listed open workflows, then save again. Confirmed reservations are not a removal blocker; ${LOCK_NOTICE.charAt(0).toLowerCase()}${LOCK_NOTICE.slice(1)}`;
  }

  if (payload.code === "FX_CURRENCY_SET_MIGRATION_REQUIRED") {
    return `This server rejected the enabled-currency change because reservation FX locks exist. Do not rewrite those reservations: ${LOCK_NOTICE.charAt(0).toLowerCase()}${LOCK_NOTICE.slice(1)} Adding a secondary currency or updating today's rate should not require a monetary migration. Refresh and retry; if this continues, the server currency rules must be updated.`;
  }

  return payload.error || "Could not save currency settings.";
};

export default function SettingsCurrencies({ businessUnitId }) {
  const { themeId, languageId } = usePmsTheme();
  const isLight = themeId === "light";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [available, setAvailable] = useState([]);
  const [adminEnabled, setAdminEnabled] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [baseCurrency, setBaseCurrency] = useState("EUR");
  const [savedBaseCurrency, setSavedBaseCurrency] = useState("EUR");
  const [selectedCurrencies, setSelectedCurrencies] = useState(["EUR"]);
  const [rates, setRates] = useState({ EUR: "1" });
  const [rateInputs, setRateInputs] = useState({ EUR: "1" });
  const [rateDirection, setRateDirection] = useState(SECONDARY_TO_BASE);
  const rateDirectionRef = useRef(SECONDARY_TO_BASE);
  const loadGenerationRef = useRef(0);
  const saveGenerationRef = useRef(0);
  const businessUnitKey = businessUnitId == null ? "" : String(businessUnitId);
  const businessUnitKeyRef = useRef(businessUnitKey);
  businessUnitKeyRef.current = businessUnitKey;
  const [effectiveFrom, setEffectiveFrom] = useState(null);
  const [rateSetId, setRateSetId] = useState(null);
  const [rateHistory, setRateHistory] = useState([]);
  const [rateHistoryError, setRateHistoryError] = useState("");
  const [loadedBusinessUnitKey, setLoadedBusinessUnitKey] = useState(null);
  const [message, setMessage] = useState(null);

  const load = useCallback(async ({ signal } = {}) => {
    if (!businessUnitId) {
      setLoadedBusinessUnitKey(null);
      setLoading(false);
      return false;
    }
    const requestedBusinessUnitId = businessUnitId;
    const requestedBusinessUnitKey = String(requestedBusinessUnitId);
    const generation = loadGenerationRef.current + 1;
    loadGenerationRef.current = generation;
    setLoadedBusinessUnitKey(null);
    setLoading(true);
    setMessage(null);
    setRateHistory([]);
    setRateHistoryError("");
    try {
      const [response, historyResponse] = await Promise.all([
        axios.get("/api/currency-settings", {
          params: { business_unit_id: requestedBusinessUnitId },
          signal,
        }),
        axios
          .get(`/api/currency-settings/${requestedBusinessUnitId}/rate-history`, {
            params: { limit: 25 },
            signal,
          })
          .then((history) => ({ data: history.data, error: "" }))
          .catch((error) => {
            if (signal?.aborted) throw error;
            return {
              data: [],
              error:
                error?.response?.data?.error ||
                "Exchange-rate history could not be loaded. Refresh to try again.",
            };
          }),
      ]);
      if (
        signal?.aborted ||
        generation !== loadGenerationRef.current ||
        requestedBusinessUnitKey !== businessUnitKeyRef.current
      ) {
        return false;
      }
      const data = response.data || {};
      const catalog = Array.isArray(data.available_currencies) ? data.available_currencies : [];
      const defaultBase = normalizeCode(
        data.base_currency_code || catalog.find((currency) => currency.code === "EUR")?.code || catalog[0]?.code
      );
      const configuredCurrencies = Array.isArray(data.currencies) && data.currencies.length
        ? data.currencies.map(normalizeCode)
        : [defaultBase].filter(Boolean);
      setAvailable(catalog);
      setAdminEnabled(Boolean(data.admin_enabled));
      setEnabled(Boolean(data.enabled));
      setBaseCurrency(defaultBase);
      setSavedBaseCurrency(defaultBase);
      setSelectedCurrencies(Array.from(new Set([defaultBase, ...configuredCurrencies])).filter(Boolean));
      const canonicalRates = { [defaultBase]: "1", ...(data.rates || {}) };
      setRates(canonicalRates);
      setRateInputs(
        Object.fromEntries(
          Object.entries(canonicalRates).map(([code, rate]) => [
            code,
            displayRate(rate, rateDirectionRef.current),
          ])
        )
      );
      setEffectiveFrom(data.effective_from || null);
      setRateSetId(data.current_rate_set_id || null);
      setRateHistory(Array.isArray(historyResponse.data) ? historyResponse.data : []);
      setRateHistoryError(historyResponse.error || "");
      setLoadedBusinessUnitKey(requestedBusinessUnitKey);
      return true;
    } catch (error) {
      if (
        signal?.aborted ||
        generation !== loadGenerationRef.current ||
        requestedBusinessUnitKey !== businessUnitKeyRef.current
      ) {
        return false;
      }
      setRateHistory([]);
      setRateHistoryError("");
      setMessage({ type: "error", text: error?.response?.data?.error || "Could not load currency settings." });
      return false;
    } finally {
      if (
        generation === loadGenerationRef.current &&
        requestedBusinessUnitKey === businessUnitKeyRef.current
      ) {
        setLoading(false);
      }
    }
  }, [businessUnitId]);

  useEffect(() => {
    const controller = new AbortController();
    saveGenerationRef.current += 1;
    setSaving(false);
    load({ signal: controller.signal });
    return () => {
      controller.abort();
      loadGenerationRef.current += 1;
    };
  }, [load]);

  const configurationReady =
    !loading && loadedBusinessUnitKey === businessUnitKey;

  const secondaryCurrencies = useMemo(
    () => selectedCurrencies.filter((code) => code !== baseCurrency),
    [baseCurrency, selectedCurrencies]
  );

  const toggleCurrency = (code) => {
    const normalized = normalizeCode(code);
    if (normalized === baseCurrency) return;
    setSelectedCurrencies((current) =>
      current.includes(normalized)
        ? current.filter((item) => item !== normalized)
        : [...current, normalized]
    );
    setRates((current) => ({ ...current, [normalized]: current[normalized] || "" }));
    setRateInputs((current) => ({ ...current, [normalized]: current[normalized] || "" }));
  };

  const changeBaseCurrency = (nextCode) => {
    const next = normalizeCode(nextCode);
    if (!next || next === baseCurrency) return;

    setBaseCurrency(next);
    setSelectedCurrencies((current) => Array.from(new Set([next, ...current])));
    // A rate expressed against the previous base has no meaning against the new
    // base. Keep only the identity rate and require every secondary rate to be
    // entered explicitly before saving.
    setRates({ [next]: "1" });
    setRateInputs({ [next]: "1" });
  };

  const changeRateDirection = (nextDirection) => {
    rateDirectionRef.current = nextDirection;
    setRateDirection(nextDirection);
    setRateInputs(
      Object.fromEntries(
        Object.entries(rates).map(([code, rate]) => [code, displayRate(rate, nextDirection)])
      )
    );
  };

  const changeRateInput = (code, value) => {
    setRateInputs((current) => ({ ...current, [code]: value }));
    setRates((current) => ({
      ...current,
      [code]: rateDirection === BASE_TO_SECONDARY ? reciprocalRate(value) : value,
    }));
  };

  const save = async () => {
    setMessage(null);
    if (!configurationReady) {
      setMessage({
        type: "error",
        text: "Load the currency settings for this Business Unit before saving.",
      });
      return;
    }
    if (!adminEnabled) {
      setMessage({
        type: "error",
        text: "Multi-currency must be enabled for this Business Unit by an administrator.",
      });
      return;
    }
    if (!baseCurrency) {
      setMessage({
        type: "error",
        text: "An administrator must assign at least one currency to this Business Unit first.",
      });
      return;
    }
    if (secondaryCurrencies.length === 0) {
      setMessage({ type: "error", text: "Select at least one secondary currency." });
      return;
    }
    const missingRate = secondaryCurrencies.find((code) => !(Number(rates[code]) > 0));
    if (missingRate) {
      setMessage({ type: "error", text: `Enter a positive exchange rate for ${missingRate}.` });
      return;
    }

    const targetBusinessUnitId = businessUnitId;
    const targetBusinessUnitKey = businessUnitKey;
    const saveGeneration = saveGenerationRef.current + 1;
    saveGenerationRef.current = saveGeneration;
    setSaving(true);
    try {
      const response = await axios.put(`/api/currency-settings/${targetBusinessUnitId}`, {
        base_currency_code: baseCurrency,
        currencies: selectedCurrencies,
        rates: {
          ...Object.fromEntries(secondaryCurrencies.map((code) => [code, rates[code]])),
          [baseCurrency]: "1",
        },
      });
      if (
        saveGeneration !== saveGenerationRef.current ||
        targetBusinessUnitKey !== businessUnitKeyRef.current
      ) {
        return;
      }
      const data = response.data || {};
      setRates(data.rates || rates);
      setEffectiveFrom(data.effective_from || null);
      setRateSetId(data.current_rate_set_id || null);
      const successText = `Currencies saved. The enabled currencies and current rates now apply to new reservations. ${LOCK_NOTICE}`;
      window.dispatchEvent(new CustomEvent("pilcore-currency-settings-updated"));
      const refreshed = await load();
      if (
        saveGeneration === saveGenerationRef.current &&
        targetBusinessUnitKey === businessUnitKeyRef.current
      ) {
        setMessage({
          type: refreshed ? "success" : "error",
          text: refreshed
            ? successText
            : "Currencies were saved, but the current settings could not be reloaded. Refresh before editing again.",
        });
      }
    } catch (error) {
      if (
        saveGeneration !== saveGenerationRef.current ||
        targetBusinessUnitKey !== businessUnitKeyRef.current
      ) {
        return;
      }
      const payload = error?.response?.data || {};
      setMessage({
        type: "error",
        text: currencySettingsErrorMessage({
          payload,
          baseCurrency,
          savedBaseCurrency,
          languageId,
        }),
      });
    } finally {
      if (
        saveGeneration === saveGenerationRef.current &&
        targetBusinessUnitKey === businessUnitKeyRef.current
      ) {
        setSaving(false);
      }
    }
  };

  return (
    <div className={`currencySettings ${isLight ? "currencySettings--light" : ""}`}>
      <style>{`
        .currencySettings{max-width:1180px;width:100%;margin:0 auto;color:#f3f8ff;display:flex;flex-direction:column;gap:18px}
        .currencySettings__hero,.currencySettings__card{border:1px solid rgba(126,220,255,.16);border-radius:20px;background:linear-gradient(145deg,rgba(22,30,52,.96),rgba(10,14,27,.96));box-shadow:0 22px 58px rgba(0,0,0,.28)}
        .currencySettings__hero{padding:24px;display:flex;justify-content:space-between;gap:20px;align-items:flex-start}
        .currencySettings__hero h2{margin:4px 0 8px;font-size:1.55rem}.currencySettings__hero p{margin:0;color:#aebbd0;max-width:760px}
        .currencySettings__eyebrow{font-size:.72rem;font-weight:900;letter-spacing:.14em;text-transform:uppercase;color:#70ddf7}
        .currencySettings__status{display:inline-flex;align-items:center;gap:8px;white-space:nowrap;font-size:.8rem;font-weight:900;padding:8px 11px;border-radius:999px;background:rgba(67,215,187,.12);color:#9ef0df}.currencySettings__status--pending{background:rgba(255,194,98,.12);color:#ffd796}.currencySettings__status--off{background:rgba(255,86,103,.12);color:#ffb1bb}
        .currencySettings__card{padding:22px}.currencySettings__card h3{font-size:1rem;margin:0 0 5px}.currencySettings__hint{color:#9eacc1;font-size:.86rem;margin:0 0 16px}
        .currencySettings__grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:12px}
        .currencySettings__currency{display:flex;align-items:center;gap:10px;padding:12px;border:1px solid rgba(255,255,255,.1);border-radius:14px;background:rgba(255,255,255,.035);cursor:pointer}
        .currencySettings__currency input{width:18px;height:18px;accent-color:#43d7bb}.currencySettings__currencyCode{font-weight:900}.currencySettings__currencyName{display:block;color:#9eacc1;font-size:.78rem}
        .currencySettings select,.currencySettings input[type=number]{width:100%;border:1px solid rgba(255,255,255,.14);border-radius:11px;background:#111a2f;color:#fff;padding:10px 12px;outline:none}
        .currencySettings__base{max-width:360px}.currencySettings__directionControl{display:grid;grid-template-columns:auto minmax(260px,420px);gap:12px;align-items:center;margin:0 0 16px;font-size:.82rem;font-weight:800;color:#9eacc1}.currencySettings__rateRows{display:grid;gap:12px}.currencySettings__rateRow{display:grid;grid-template-columns:minmax(190px,1fr) minmax(180px,260px);align-items:center;gap:16px;padding:14px;border-radius:14px;background:rgba(255,255,255,.035)}
        .currencySettings__equation{font-weight:800}.currencySettings__equation small{display:block;color:#9eacc1;font-weight:500;margin-top:3px}
        .currencySettings__message{display:flex;gap:9px;align-items:flex-start;border-radius:13px;padding:12px 14px;font-weight:700}.currencySettings__message--error{background:rgba(255,86,103,.12);color:#ffb1bb}.currencySettings__message--success{background:rgba(67,215,187,.12);color:#9ef0df}
        .currencySettings__footer{display:flex;align-items:center;justify-content:space-between;gap:16px}.currencySettings__meta{color:#93a2b7;font-size:.8rem}.currencySettings__actions{display:flex;gap:10px}.currencySettings__button{display:inline-flex;align-items:center;gap:8px;border-radius:11px;padding:10px 15px;border:1px solid rgba(255,255,255,.14);background:#18233b;color:#fff;font-weight:800}.currencySettings__button--primary{background:linear-gradient(135deg,#24bda3,#2388ad);border-color:transparent}.currencySettings__button:disabled{opacity:.55}
        .currencySettings__history{display:grid;gap:10px}.currencySettings__historyRow{display:grid;grid-template-columns:minmax(170px,.65fr) minmax(260px,1.6fr) minmax(180px,1fr);gap:16px;align-items:start;padding:13px 14px;border-radius:14px;background:rgba(255,255,255,.035)}.currencySettings__historyDate{font-weight:800}.currencySettings__historyId,.currencySettings__historyNote{color:#93a2b7;font-size:.78rem}.currencySettings__historyRates{display:flex;gap:7px;flex-wrap:wrap}.currencySettings__rateChip{padding:4px 8px;border-radius:999px;background:rgba(67,215,187,.11);color:#9ef0df;font-size:.78rem;font-weight:800}
        .currencySettings--light{color:#183645}.currencySettings--light .currencySettings__hero,.currencySettings--light .currencySettings__card{background:rgba(255,255,255,.82);border-color:rgba(24,54,69,.1);box-shadow:0 20px 48px rgba(86,65,39,.08)}.currencySettings--light .currencySettings__hero p,.currencySettings--light .currencySettings__hint,.currencySettings--light .currencySettings__currencyName,.currencySettings--light .currencySettings__equation small,.currencySettings--light .currencySettings__meta{color:#6b777c}.currencySettings--light select,.currencySettings--light input[type=number]{background:#fff;color:#183645;border-color:rgba(24,54,69,.15)}.currencySettings--light .currencySettings__currency,.currencySettings--light .currencySettings__rateRow{background:rgba(24,54,69,.035);border-color:rgba(24,54,69,.08)}
        .currencySettings--light .currencySettings__historyRow{background:rgba(24,54,69,.035)}.currencySettings--light .currencySettings__historyId,.currencySettings--light .currencySettings__historyNote{color:#6b777c}.currencySettings--light .currencySettings__rateChip{background:rgba(25,145,123,.1);color:#167b69}.currencySettings--light .currencySettings__status{color:#167b69}.currencySettings--light .currencySettings__status--pending{color:#94610b}.currencySettings--light .currencySettings__status--off{color:#a43b49}
        @media(max-width:700px){.currencySettings__hero{flex-direction:column}.currencySettings__directionControl,.currencySettings__rateRow,.currencySettings__historyRow{grid-template-columns:1fr}.currencySettings__footer{align-items:flex-start;flex-direction:column}.currencySettings__actions{width:100%}.currencySettings__button{flex:1;justify-content:center}}
      `}</style>

      <section className="currencySettings__hero">
        <div>
          <div className="currencySettings__eyebrow">Currencies</div>
          <h2>Currencies and exchange rates</h2>
          <p>
            Choose the currencies this hotel uses and keep their exchange rates up to date. New settings apply to
            new reservations; existing reservations keep their original currency, exchange rate and confirmed base
            amounts.
          </p>
        </div>
        <div
          className={`currencySettings__status ${
            !adminEnabled
              ? "currencySettings__status--off"
              : enabled
                ? ""
                : "currencySettings__status--pending"
          }`}
        >
          {!adminEnabled
            ? "Disabled by administrator"
            : enabled
              ? "Enabled by administrator · active"
              : "Enabled by administrator · setup pending"}
        </div>
      </section>

      {message && (
        <div className={`currencySettings__message currencySettings__message--${message.type}`}>
          {message.type === "success" ? <FiCheck /> : <FiAlertCircle />}
          <span>{message.text}</span>
        </div>
      )}

      {!loading && !adminEnabled && (
        <div className="currencySettings__message currencySettings__message--error">
          <FiAlertCircle />
          <span>This configuration is read-only until an administrator enables multi-currency for the Business Unit.</span>
        </div>
      )}

      <section className="currencySettings__card">
        <h3>Base currency</h3>
        <p className="currencySettings__hint">
          This is the hotel&apos;s accounting currency. Choosing another base is a real monetary-data change, not the
          way to add a secondary currency, and may require a separately reviewed migration after monetary activity
          exists. Only currencies assigned by an administrator are available.
        </p>
        <div className="currencySettings__base">
          <select value={baseCurrency} onChange={(event) => changeBaseCurrency(event.target.value)} disabled={!configurationReady || saving || enabled || !adminEnabled}>
            {available.map((currency) => (
              <option key={currency.code} value={currency.code}>{currency.code} · {currency.name}</option>
            ))}
          </select>
        </div>
        {!available.length && (
          <div className="currencySettings__message currencySettings__message--error" style={{ marginTop: 14 }}>
            <FiAlertCircle />
            <span>An administrator must assign currencies to this Business Unit in Admin → Currencies.</span>
          </div>
        )}
      </section>

      <section className="currencySettings__card">
        <h3>Enabled currencies</h3>
        <p className="currencySettings__hint">
          Activate any subset of the currencies assigned by an administrator. Room rates may use any enabled
          currency; non-base amounts are converted to {baseCurrency} using the configured exchange rate and locked
          when the reservation is created. Adding a secondary currency does not change existing reservations.
          Disabling one is blocked only while an active catalog item or open draft, quote or payment still depends on
          it.
        </p>
        <div className="currencySettings__grid">
          {available.map((currency) => {
            const code = normalizeCode(currency.code);
            const selected = selectedCurrencies.includes(code);
            return (
              <label key={code} className="currencySettings__currency">
                <input type="checkbox" checked={selected} disabled={!configurationReady || code === baseCurrency || saving || !adminEnabled} onChange={() => toggleCurrency(code)} />
                <span><span className="currencySettings__currencyCode">{code}</span><span className="currencySettings__currencyName">{currency.name}</span></span>
              </label>
            );
          })}
        </div>
      </section>

      <section className="currencySettings__card">
        <h3>Current exchange rates</h3>
        <p className="currencySettings__hint">
          Choose whichever quotation direction is easiest for the person entering the rates. Updating a rate creates
          a new version for future reservations; it never rewrites an existing reservation&apos;s locked rate.
        </p>
        <label className="currencySettings__directionControl">
          Rate input format
          <select
            value={rateDirection}
            onChange={(event) => changeRateDirection(event.target.value)}
            disabled={!configurationReady || saving || !adminEnabled}
          >
            <option value={SECONDARY_TO_BASE}>1 secondary currency = … {baseCurrency}</option>
            <option value={BASE_TO_SECONDARY}>1 {baseCurrency} = … secondary currency</option>
          </select>
        </label>
        <div className="currencySettings__rateRows">
          {secondaryCurrencies.map((code) => (
            <div key={code} className="currencySettings__rateRow">
              <div className="currencySettings__equation">
                {rateDirection === BASE_TO_SECONDARY
                  ? `1 ${baseCurrency} = … ${code}`
                  : `1 ${code} = … ${baseCurrency}`}
                <small>Changes apply to new reservations after saving.</small>
              </div>
              <input
                type="number"
                min="0.000000000001"
                step="0.000001"
                value={rateInputs[code] || ""}
                onChange={(event) => changeRateInput(code, event.target.value)}
                disabled={!configurationReady || saving || !adminEnabled}
                placeholder={
                  rateDirection === BASE_TO_SECONDARY
                    ? `${code} units per 1 ${baseCurrency}`
                    : `${baseCurrency} units per 1 ${code}`
                }
              />
            </div>
          ))}
          {!secondaryCurrencies.length && <div className="currencySettings__hint">Select a secondary currency to enter its exchange rate.</div>}
        </div>
      </section>

      <section className="currencySettings__card">
        <h3>Exchange-rate history</h3>
        <p className="currencySettings__hint">
          Previous exchange-rate versions are read-only audit records. They preserve the rates used by earlier
          reservations, but their existence does not block adding a currency or updating today&apos;s rate.
        </p>
        <div className="currencySettings__history">
          {!rateHistoryError && rateHistory.map((set) => {
            const setBase = normalizeCode(set.base_currency_code);
            const setRates = Array.isArray(set.rates) ? set.rates : [];
            return (
              <div key={set.id} className="currencySettings__historyRow">
                <div>
                  <div className="currencySettings__historyDate">
                    {set.effective_from ? new Date(set.effective_from).toLocaleString() : "Unknown date"}
                  </div>
                  <div className="currencySettings__historyId">Base currency: {setBase}</div>
                </div>
                <div className="currencySettings__historyRates">
                  {setRates.map((rate) => (
                    <span key={rate.currency_code} className="currencySettings__rateChip">
                      {rateDirection === BASE_TO_SECONDARY
                        ? `1 ${setBase} = ${reciprocalRate(rate.base_units_per_currency)} ${rate.currency_code}`
                        : `1 ${rate.currency_code} = ${rate.base_units_per_currency} ${setBase}`}
                    </span>
                  ))}
                </div>
                <div className="currencySettings__historyNote">Rate updated</div>
              </div>
            );
          })}
          {rateHistoryError && (
            <div className="currencySettings__message currencySettings__message--error">
              <FiAlertCircle />
              <span>{rateHistoryError}</span>
            </div>
          )}
          {!rateHistoryError && !rateHistory.length && (
            <div className="currencySettings__hint">No exchange-rate versions have been created yet.</div>
          )}
        </div>
      </section>

      <div className="currencySettings__footer">
        <div className="currencySettings__meta">
          {rateSetId ? "Current exchange rates" : "No exchange rates saved yet"}
          {effectiveFrom ? ` · effective from ${new Date(effectiveFrom).toLocaleString()}` : ""}
        </div>
        <div className="currencySettings__actions">
          <button type="button" className="currencySettings__button" onClick={() => load()} disabled={loading || saving}><FiRefreshCw />Refresh</button>
          <button type="button" className="currencySettings__button currencySettings__button--primary" onClick={save} disabled={!configurationReady || saving || !adminEnabled}><FiSave />{saving ? "Saving…" : "Save currencies"}</button>
        </div>
      </div>
    </div>
  );
}
